import { clusterApiUrl, type SimulatedTransactionResponse, Transaction, TransactionInstruction } from "@solana/web3.js";
import { RPC_ENDPOINT } from "@solomon-apps/config";
import { Base58Util } from "@solomon-labs/base58";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { TOKEN_2022_PROGRAM_ID_STRING } from "@solomon-labs/constants";
import { RpcClient } from "@solomon-labs/solana-rpc-client";

import type { ClaimChain } from "./ClaimFlow";
import type { ClaimAttempt } from "./claimTypes";

const fingerprints: Record<string, string> = {
    "mainnet-beta": "5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
    devnet: "EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
    testnet: "4uhcVJyU9pJkvQyS88uRDiswHXSCkY3zQawwpjk2NsNY"
};
export class ClaimBrowserChain implements ClaimChain {
    private readonly connections = new Map<string, RpcClient>();
    private request<T>(rpc: RpcClient, method: string, params: unknown[] = []): Promise<T> {
        return rpc.request<T>(method, params, { timeout: 15_000, signal: AbortSignal.timeout(15_000) });
    }
    private client(endpoint: string): RpcClient {
        return new RpcClient(endpoint as ConstructorParameters<typeof RpcClient>[0], undefined, { maxRetries: 3 });
    }
    private async connection(network: string): Promise<RpcClient> {
        if (!Object.hasOwn(fingerprints, network)) throw new Error("Unsupported reward network");
        const cached = this.connections.get(network);
        if (cached) return cached;
        let rpc = this.client(RPC_ENDPOINT);
        if (!(await this.request<string>(rpc, "getGenesisHash")).startsWith(fingerprints[network])) {
            rpc = this.client(clusterApiUrl(network as "mainnet-beta" | "devnet" | "testnet"));
            if (!(await this.request<string>(rpc, "getGenesisHash")).startsWith(fingerprints[network]))
                throw new Error("RPC network does not match this claim");
        }
        this.connections.set(network, rpc);
        return rpc;
    }
    private transaction(attempt: ClaimAttempt): Transaction {
        return Transaction.from(Base58Util.decode(attempt.transaction as Parameters<typeof Base58Util.decode>[0]));
    }
    async estimate(attempt: ClaimAttempt): Promise<string> {
        const rpc = await this.connection(attempt.network);
        const transaction = this.transaction(attempt);
        const fee = await this.request<{ value: number | null }>(rpc, "getFeeForMessage", [
            transaction.serializeMessage().toString("base64"),
            { commitment: "confirmed" }
        ]);
        if (fee.value === null) throw new Error("Transaction expired. Prepare the claim again.");
        const claim = transaction.instructions.at(-1);
        if (claim?.keys.length !== 11) throw new Error("Invalid claim instruction");
        const {
            value: [claimAccount, destination]
        } = await this.request<{ value: (object | null)[] }>(rpc, "getMultipleAccounts", [
            [claim.keys[3].pubkey.toBase58(), claim.keys[6].pubkey.toBase58()],
            { commitment: "confirmed", encoding: "base64" }
        ]);
        // The installed vendor estimator omits Claim PDA rent and idempotent ATA creation.
        const rent = (space: number): Promise<number> =>
            this.request(rpc, "getMinimumBalanceForRentExemption", [space, { commitment: "confirmed" }]);
        let amount = new BigDecimal(String(fee.value));
        if (!claimAccount) amount = amount.add(new BigDecimal(String(await rent(10))));
        if (!destination) {
            let space = 165;
            const tokenProgram = claim.keys[7].pubkey;
            if (tokenProgram.toBase58() === TOKEN_2022_PROGRAM_ID_STRING) {
                // Ask Token-2022 for the size required by this mint plus the ATA's ImmutableOwner extension.
                const sizeQuery = new Transaction({
                    feePayer: transaction.feePayer,
                    recentBlockhash: transaction.recentBlockhash
                }).add(
                    new TransactionInstruction({
                        programId: tokenProgram,
                        keys: [{ pubkey: claim.keys[4].pubkey, isSigner: false, isWritable: false }],
                        data: Buffer.from([21, 7, 0])
                    })
                );
                const simulation = await this.request<{ value: SimulatedTransactionResponse }>(
                    rpc,
                    "simulateTransaction",
                    [
                        sizeQuery
                            .serialize({ requireAllSignatures: false, verifySignatures: false })
                            .toString("base64"),
                        { encoding: "base64", sigVerify: false, commitment: "confirmed" }
                    ]
                );
                const returned = simulation.value.returnData;
                if (
                    simulation.value.err ||
                    returned?.programId !== tokenProgram.toBase58() ||
                    returned.data[1] !== "base64"
                )
                    throw new Error("Account creation cost could not be verified");
                const data = Buffer.from(returned.data[0], "base64");
                if (data.length !== 8) throw new Error("Invalid token account size");
                const bytes = data.readBigUInt64LE();
                if (bytes < 165n || bytes > 10485760n) throw new Error("Unsupported token account size");
                space = Number(bytes);
            }
            amount = amount.add(new BigDecimal(String(await rent(space))));
        }
        return amount.toFixed(0);
    }
    async isValid(attempt: ClaimAttempt): Promise<boolean> {
        const rpc = await this.connection(attempt.network);
        return (
            (
                await this.request<{ value: boolean }>(rpc, "isBlockhashValid", [
                    attempt.recentBlockhash,
                    { commitment: "confirmed" }
                ])
            ).value === true
        );
    }
    async submit(attempt: ClaimAttempt, bytes: Uint8Array): Promise<string> {
        return this.request(await this.connection(attempt.network), "sendTransaction", [
            Buffer.from(bytes).toString("base64"),
            { encoding: "base64", skipPreflight: false, maxRetries: 3, preflightCommitment: "confirmed" }
        ]);
    }
}
